import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import {
  CATEGORIES,
  FILE_POPULATIONS,
  type Files,
  type FilesQuery,
  type ItemCategory,
  type Metrics,
  diagnosticsUrl,
  getFiles,
  getMetrics,
  inventoryUrl,
  jobPath,
  message,
  seconds,
} from "./runsClient";

const PAGE = 50;
const REFRESH_MS = 5000;
const LEVEL_LABELS: Record<string, string> = {
  files: "Files",
  parsed_documents: "Parsed documents",
  segments: "Knowledge segments",
  observations: "Observations",
  claims: "Data claims",
  outputs: "Outputs (export items)",
};
const STAGE_COUNTS = [
  "eligible",
  "queued",
  "leased",
  "succeeded",
  "reused",
  "failed",
  "held",
  "cancelled",
  "awaiting_operator",
];

function filesQuery(params: URLSearchParams): FilesQuery {
  return {
    category: (params.get("category") || undefined) as ItemCategory | undefined,
    stage: params.get("stage") || undefined,
    q: params.get("q") || undefined,
    awaiting_operator: params.get("awaiting") === "1" ? true : undefined,
    offset: Number(params.get("offset") ?? 0) || 0,
    limit: PAGE,
  };
}

function breakdown(counts: Record<string, number>): string {
  return Object.entries(counts)
    .map(([state, n]) => `${state} ${n}`)
    .join(" · ");
}

/** One Corpus Run's dashboard (task 4030.8; MER-BATCH-009, MER-BATCH-010, MER-D-021,
 * MER-DEP-009): counts per level, the failed, held, duplicate, excluded, and unresolved
 * populations, stages, failures, budget, workers, and the database reconciliation, with every
 * file linked to its jobs and every job to its diagnostic record. Refreshes while the run is
 * live. */
export function RunDashboardPage() {
  const { runId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [files, setFiles] = useState<Files | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const search = params.toString();

  useEffect(() => {
    let live = true;
    getMetrics(runId)
      .then((found) => {
        if (live) {
          setMetrics(found);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [runId, tick]);

  useEffect(() => {
    let live = true;
    getFiles(runId, filesQuery(new URLSearchParams(search)))
      .then((found) => {
        if (live) setFiles(found);
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [runId, tick, search]);

  const terminal = metrics?.run.terminal ?? true;
  useEffect(() => {
    if (terminal) return undefined;
    const timer = window.setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [terminal]);

  const choose = useCallback(
    (next: Record<string, string | undefined>) => {
      const merged = new URLSearchParams();
      for (const [key, value] of Object.entries(next)) {
        if (value) merged.set(key, value);
      }
      setParams(merged);
    },
    [setParams],
  );

  function onFilter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || undefined;
    choose({
      category: text("category"),
      stage: text("stage"),
      q: text("q"),
      awaiting: form.get("awaiting") === "on" ? "1" : undefined,
    });
  }

  function page(offset: number) {
    const next = new URLSearchParams(search);
    if (offset > 0) next.set("offset", String(offset));
    else next.delete("offset");
    setParams(next);
  }

  if (error && !metrics) return <p role="alert">Could not load the Corpus Run: {error}</p>;
  if (!metrics) return <p>Loading…</p>;

  const filter = filesQuery(params);
  const offset = filter.offset ?? 0;
  const mismatched = metrics.checks.filter((c) => !c.ok);

  return (
    <section aria-labelledby="run-title">
      <p>
        <Link to="/corpus-runs">All Corpus Runs</Link>
      </p>
      <h1 id="run-title">Corpus Run {metrics.run_key}</h1>
      <p data-testid="run-state">
        <strong className={`status-${metrics.run.state}`}>{metrics.run.state}</strong>
        {metrics.run.state_reason ? ` (${metrics.run.state_reason})` : ""} ·{" "}
        {metrics.run.stages_finished} of {metrics.run.stages} stages finished · elapsed{" "}
        {seconds(metrics.run.elapsed_seconds)} · updated {metrics.generated_at}
        {metrics.run.terminal ? " · final accounting" : " · live, refreshing"}
      </p>
      <p>
        <button type="button" onClick={() => setTick((t) => t + 1)}>
          Refresh
        </button>{" "}
        <a href={diagnosticsUrl(metrics.run_id)} download>
          Download diagnostics
        </a>{" "}
        · <Link to={inventoryUrl(metrics.run_id)}>Corpus inventory for this run</Link>
      </p>
      {error && <p role="alert">Refresh failed: {error}</p>}
      {mismatched.length > 0 ? (
        <p role="alert" data-testid="reconciliation">
          Counts do not reconcile with the database:{" "}
          {mismatched.map((c) => `${c.name} (database ${c.expected}, shown ${c.actual})`).join("; ")}
        </p>
      ) : (
        <p data-testid="reconciliation">
          All {metrics.checks.length} counts reconcile with the database.
        </p>
      )}

      <h2>Populations</h2>
      <p>
        {metrics.total_files} files: {metrics.files.processed ?? 0} processed
        {metrics.files.in_progress ? `, ${metrics.files.in_progress} in progress` : ""}
        {metrics.awaiting_operator > 0 &&
          ` · ${metrics.awaiting_operator} awaiting an operator decision`}
      </p>
      <ul aria-label="File populations" className="populations">
        {FILE_POPULATIONS.map((name) => (
          <li key={name}>
            <button
              type="button"
              aria-pressed={filter.category === name}
              onClick={() => choose({ category: name })}
            >
              {name}: <strong>{metrics.populations.files[name] ?? 0}</strong>
            </button>
          </li>
        ))}
      </ul>
      <ul aria-label="Claim populations" className="populations">
        {Object.entries(metrics.populations.claims).map(([name, count]) => (
          <li key={name}>
            {name} claims: <strong>{count}</strong>
          </li>
        ))}
      </ul>

      <h2>Levels</h2>
      <table className="grid" aria-label="Levels">
        <thead>
          <tr>
            <th>Level</th>
            <th>Total</th>
            <th>By state</th>
          </tr>
        </thead>
        <tbody>
          {metrics.levels.map((level) => (
            <tr key={level.level}>
              <td>{LEVEL_LABELS[level.level] ?? level.level}</td>
              <td>{level.total}</td>
              <td>{breakdown(level.by_state)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Stages</h2>
      <table className="grid" aria-label="Stages">
        <thead>
          <tr>
            <th>Stage</th>
            <th>State</th>
            {STAGE_COUNTS.map((name) => (
              <th key={name}>{name.replace("_", " ")}</th>
            ))}
            <th>Retries</th>
            <th>Duration p50 / p95</th>
            <th>Error classes</th>
          </tr>
        </thead>
        <tbody>
          {metrics.stages.map((stage) => (
            <tr key={stage.ordinal}>
              <td>
                {stage.ordinal}. {stage.stage}@{stage.version}
                {stage.barrier ? " (barrier)" : ""}
              </td>
              <td className={`status-${stage.state}`}>
                {stage.state}
                {stage.waiting_for_barrier ? ", waiting for upstream" : ""}
                {stage.budget_request ? `, budget decision: ${stage.budget_request}` : ""}
              </td>
              {STAGE_COUNTS.map((name) => (
                <td key={name}>{stage.counts[name] ?? 0}</td>
              ))}
              <td>
                {stage.retried_jobs} jobs, {stage.extra_attempts} extra attempts
              </td>
              <td>
                {seconds(stage.duration_p50_seconds)} / {seconds(stage.duration_p95_seconds)}
              </td>
              <td>{breakdown(stage.error_classes) || "none"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Awaiting a decision</h2>
      {metrics.failures.open.length === 0 ? (
        <p>No open dead letters.</p>
      ) : (
        <table className="grid" aria-label="Open dead letters">
          <thead>
            <tr>
              <th>File</th>
              <th>Stage</th>
              <th>Error class</th>
              <th>Error</th>
              <th>Attempts</th>
              <th>Since</th>
              <th>Diagnostic record</th>
            </tr>
          </thead>
          <tbody>
            {metrics.failures.open.map((dead) => (
              <tr key={dead.dead_letter_id}>
                <td>{dead.path ?? dead.subject_key}</td>
                <td>{dead.stage}</td>
                <td>{dead.error_class}</td>
                <td>{dead.message ?? dead.reason}</td>
                <td>{dead.attempts}</td>
                <td>{dead.created_at}</td>
                <td>
                  <Link to={jobPath(metrics.run_id, dead.job_id)}>Open job</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p>
        Dead letters: {breakdown(metrics.failures.by_state)}
        {Object.keys(metrics.failures.by_error_class).length > 0 &&
          ` · by class: ${breakdown(metrics.failures.by_error_class)}`}
      </p>

      <h2>Files</h2>
      <form onSubmit={onFilter} aria-label="File filters" key={search}>
        <label>
          Category{" "}
          <select name="category" defaultValue={filter.category ?? ""}>
            <option value="">Any</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c} ({metrics.files[c] ?? 0})
              </option>
            ))}
          </select>
        </label>
        <label>
          Stage{" "}
          <select name="stage" defaultValue={filter.stage ?? ""}>
            <option value="">Any</option>
            {metrics.stages.map((s) => (
              <option key={s.stage} value={s.stage}>
                {s.stage}
              </option>
            ))}
          </select>
        </label>
        <label>
          Path contains <input name="q" defaultValue={filter.q ?? ""} />
        </label>
        <label>
          <input name="awaiting" type="checkbox" defaultChecked={filter.awaiting_operator} />{" "}
          Awaiting an operator
        </label>
        <button type="submit">Filter</button>{" "}
        <button type="button" onClick={() => choose({})}>
          Clear
        </button>
      </form>
      {files && (
        <>
          <p data-testid="files-shown">
            Showing {files.items.length === 0 ? 0 : offset + 1}–{offset + files.items.length} of{" "}
            {files.filtered} matching ({files.total} in the run)
          </p>
          <table className="grid" aria-label="Files">
            <thead>
              <tr>
                <th>Path</th>
                <th>Category</th>
                <th>Reason</th>
                {files.stages.map((stage) => (
                  <th key={stage}>{stage}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.items.map((item) => (
                <tr key={item.item_key}>
                  <td>{item.path}</td>
                  <td>
                    {item.category}
                    {item.awaiting_operator ? " (awaiting operator)" : ""}
                  </td>
                  <td>
                    {item.reason_code}: {item.reason}
                  </td>
                  {files.stages.map((stage) => {
                    const job = item.jobs[stage];
                    return (
                      <td key={stage}>
                        {job ? (
                          <Link to={jobPath(files.run_id, job.job_id)}>
                            {job.status}
                            {job.reused ? " (reused)" : ""}
                            {job.attempts > 1 ? ` · ${job.attempts} attempts` : ""}
                            {job.error_class ? ` · ${job.error_class}` : ""}
                          </Link>
                        ) : (
                          "–"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <nav aria-label="Pages">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => page(Math.max(0, offset - PAGE))}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + PAGE >= files.filtered}
              onClick={() => page(offset + PAGE)}
            >
              Next
            </button>
          </nav>
        </>
      )}

      <h2>Queue and retries</h2>
      <p data-testid="queue">
        {breakdown(metrics.queue.by_status)} · {metrics.queue.ready} ready, {metrics.queue.delayed}{" "}
        in backoff, oldest ready {seconds(metrics.queue.oldest_ready_seconds)} ·{" "}
        {metrics.queue.expired_leases} expired leases
      </p>
      <p>
        {metrics.retries.retried_jobs} jobs retried ({metrics.retries.extra_attempts} extra
        attempts) · {metrics.retries.retry_controls} retry controls ·{" "}
        {metrics.retries.requeued_dead_letters} dead letters requeued
        {metrics.orphan_jobs > 0 && ` · ${metrics.orphan_jobs} jobs outside the plan`}
      </p>

      <h2>Budget</h2>
      <p data-testid="budget">
        Spent ${metrics.budget.spent_usd} · reserved ${metrics.budget.outstanding_usd} · hard
        limit {metrics.budget.hard_limit_usd ? `$${metrics.budget.hard_limit_usd}` : "none"} ·
        soft limit {metrics.budget.soft_limit_usd ? `$${metrics.budget.soft_limit_usd}` : "none"}
        {metrics.budget.remaining_usd !== null && ` · remaining $${metrics.budget.remaining_usd}`}{" "}
        · {metrics.budget.calls} model calls · {metrics.budget.reservations} reservations (
        {metrics.budget.reservations_open} open)
      </p>
      {metrics.budget.open_requests.length > 0 && (
        <ul aria-label="Budget decisions">
          {metrics.budget.open_requests.map((r) => (
            <li key={r.request_id}>
              {r.stage}: {r.kind} ({r.detail})
            </li>
          ))}
        </ul>
      )}

      <h2>Workers</h2>
      {metrics.workers.length === 0 ? (
        <p>No worker has run or holds this run's work, and none is sending heartbeats.</p>
      ) : (
        <table className="grid" aria-label="Workers">
          <thead>
            <tr>
              <th>Worker</th>
              <th>Health</th>
              <th>Last seen</th>
              <th>Leases held</th>
              <th>Expired leases</th>
              <th>Attempts</th>
              <th>Succeeded</th>
              <th>Failed</th>
            </tr>
          </thead>
          <tbody>
            {metrics.workers.map((w) => (
              <tr key={w.worker_id}>
                <td>
                  {w.worker_id}
                  {w.version ? ` (${w.version})` : ""}
                </td>
                <td className={w.alive ? `status-${w.status}` : "status-stale"}>
                  {w.alive ? w.status : w.status === "no_heartbeat" ? "no heartbeat" : "stale"}
                </td>
                <td>{w.last_seen_at ?? ""}</td>
                <td>{w.leases}</td>
                <td>{w.expired_leases}</td>
                <td>{w.attempts}</td>
                <td>{w.succeeded}</td>
                <td>{w.failed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Dispositions</h2>
      <p>{breakdown(metrics.dispositions)}</p>

      <h2>Reconciliation</h2>
      <table className="grid" aria-label="Reconciliation checks">
        <thead>
          <tr>
            <th>Check</th>
            <th>Database</th>
            <th>Dashboard</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {metrics.checks.map((check) => (
            <tr key={check.name}>
              <td title={check.name}>{check.description}</td>
              <td>{check.expected}</td>
              <td>{check.actual}</td>
              <td className={check.ok ? "status-ok" : "status-failed"}>
                {check.ok ? "reconciled" : "mismatch"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
